import {ReactElement, useCallback, useMemo} from "react";
import {FormattedMessage} from "react-intl";
import {EditorGroup} from "./EditorGroup";
import {SubTitle} from "components/UIComponents/SubTitle";
import {useAppDispatch, useAppSelector} from "lib/redux/hooks";
import {menuEditorActions, menuEditorSelectors} from "../editorSlice";
import {PillSelector} from "components/UIComponents/PillSelector";
import {FormGroup} from "components/UIComponents/FormGroup";
import {FormInput} from "components/UIComponents/FormInput";
import {CoverPage} from "../MenuDocumentModel";
import {ImagePicker} from "./image/ImagePicker";
import {authSelectors} from "../../auth/authSlice";
import {fetchImageCollection, fetchUserImages, ImageGroup} from "./image/imageCollections";
import { createDocumentHandler } from "../documentHandlerUtils";

export interface CoverPageEditorProps {
}

export function CoverPageEditor(props: CoverPageEditorProps): ReactElement | null {
    const dispatch = useAppDispatch();

    const document = useAppSelector(menuEditorSelectors.document);
    const collection = useMemo(()=>{
        if (!document) return null;
        return createDocumentHandler(document).getCoverPageCollection()
    }, [document]);
    const userId = useAppSelector(authSelectors.customer);

    const fetchCoverImages = useCallback((): Promise<ImageGroup[]> => {
        if (userId == null || collection == null) {
            return Promise.resolve([]);
        } else {
            return fetchImageCollection({
                userId: userId,
                collection: collection
            })
        }
    }, [userId, collection]);

    const fetchLogoImages = useCallback(async (): Promise<ImageGroup[]> => {
        if (userId == null) {
            return Promise.resolve([]);
        } else {
            const images = await fetchUserImages(userId, 'logo');
            return [{
                name: "Logos",
                images: images
            }];
        }
    }, [userId]);


    if (document == null || document.coverPage == null || userId == null) {
        return null;
    }

    const setCoverPageTitle = (title: string) => dispatch(menuEditorActions.setCoverPageTitle(title));
    const setCoverPageVariant = (variant: '1' | '2' | '3') => dispatch(menuEditorActions.setCoverPageVariant(variant));
    const setCoverPageImage = (imgUrl: string | null) => {
        if (document.coverPage == null || imgUrl == null) {
            return;
        }

        const coverPage: CoverPage = {
            title: document.coverPage.title,
            coverImageUrl: imgUrl,
            logoImageUrl: document.coverPage.logoImageUrl,
        };
        dispatch(menuEditorActions.setCoverPage(coverPage));
    }
    const setCoverPageLogo = (imgUrl: string | null) => {
        if (document.coverPage == null) {
            return;
        }

        const coverPage: CoverPage = {
            title: document.coverPage.title,
            coverImageUrl: document.coverPage.coverImageUrl,
            logoImageUrl: imgUrl
        };
        dispatch(menuEditorActions.setCoverPage(coverPage));
    }

    return (
        <div className="flex flex-col">
            <div className="px-6">
                <SubTitle><FormattedMessage id="editor.coverPage.editorTitle"/></SubTitle>
            </div>

            <EditorGroup>
                <FormattedMessage id="editor.coverPage.variant"/>

                <PillSelector
                    options={[1, 2, 3]}
                    selected={parseInt(document.coverPage?.variant || '1')}
                    onSelect={v => setCoverPageVariant(v.toString() as ('1' | '2' | '3'))}
                    getLabel={v => v.toString()}
                />
            </EditorGroup>

            <EditorGroup>
                <FormGroup groupTitle={<FormattedMessage id="editor.coverPage.text"/>}>
                    <FormInput
                        value={document.coverPage?.title}
                        onChange={e => setCoverPageTitle(e.target.value)}
                    />
                </FormGroup>
            </EditorGroup>

            <EditorGroup>
                <FormGroup groupTitle={<FormattedMessage id="editor.coverPage.cover"/>}>
                    <ImagePicker
                        images={fetchCoverImages}
                        currentImage={document.coverPage?.coverImageUrl ?? null}
                        onPick={setCoverPageImage}
                        allowClear={false}
                        upload={{
                            userId: userId,
                            collection: 'cover',
                            validate: i => {
                                if (i.stats.width <= 2490 || i.stats.height <= 3510) {
                                    return {
                                        valid: true,
                                        title: <FormattedMessage id="editor.image.validation.resolution.title"/>,
                                        text: <FormattedMessage id="editor.image.validation.resolution.text"/>
                                    };
                                }
                                if (i.stats.fileSize >= 6 * 1024 * 1024) {
                                    return {
                                        valid: false,
                                        title: <FormattedMessage id="editor.image.validation.fileSize.title"/>,
                                        text: <FormattedMessage id="editor.image.validation.fileSize.text" />
                                    };
                                }
                                return {valid: true};
                            }
                        }}
                    />
                </FormGroup>
                <FormGroup groupTitle={<FormattedMessage id="editor.coverPage.logo"/>}>
                    <ImagePicker
                        images={fetchLogoImages}
                        currentImage={document.coverPage?.logoImageUrl ?? null}
                        onPick={setCoverPageLogo}
                        allowClear={true}
                        upload={{
                            userId: userId,
                            collection: 'logo'
                        }}
                    />
                </FormGroup>
            </EditorGroup>
        </div>
    );
}